/**
 * Reference rail docked to the left of the tutorial board on wide layouts. Mirrors the content
 * of the Instructions modal (pieces, captures, victory conditions, progressions) so a learner
 * watching the tutorial doesn't have to open a modal to consult the rules. On narrow layouts
 * this component isn't rendered — TutorialMode instead opens the existing Instructions modal
 * via a "?" button.
 */

import { STRATEGIES } from "./strategies";
import { StartingBoardSVG } from "./StartingBoardSVG";
import {
  CaptureRow,
  PieceRow,
  ProgressionDiagram,
  Section,
  StrategyList,
  VictoryRow,
} from "./InstructionsSections";
import { list, paragraph } from "./InstructionsStyles";
import { theme, FONT_RUNE } from "./theme";

export const RAIL_EXPANDED_PX = 260;
export const RAIL_COLLAPSED_PX = 40;

export function TutorialReferenceRail({
  collapsed,
  onToggleCollapse,
}: {
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  if (collapsed) {
    return (
      <div
        style={{
          width: RAIL_COLLAPSED_PX,
          flexShrink: 0,
          background: theme.bgElev,
          borderRight: `1px solid ${theme.boardEdge}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "12px 0",
          gap: 12,
        }}
      >
        <button
          onClick={onToggleCollapse}
          aria-expanded={false}
          aria-label="Expand reference panel"
          title="Expand reference"
          style={collapsedButton}
        >
          ?
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        width: RAIL_EXPANDED_PX,
        flexShrink: 0,
        background: theme.bgElev,
        borderRight: `1px solid ${theme.boardEdge}`,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Sticky header with collapse toggle. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderBottom: `1px solid ${theme.boardEdge}`,
          position: "sticky",
          top: 0,
          background: theme.bgElev,
          zIndex: 1,
        }}
      >
        <span
          className="rune"
          style={{ color: theme.accentSoft, fontSize: 12, letterSpacing: 2 }}
        >
          REFERENCE
        </span>
        <button
          onClick={onToggleCollapse}
          aria-expanded={true}
          aria-label="Collapse reference panel"
          title="Collapse"
          style={expandedToggleButton}
        >
          ◀
        </button>
      </div>

      {/* Scrollable content. Sections use native <details>, so each expand/collapse is
          independent and no controlled state is needed here. The two most reference-worthy
          sections start open. */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 14px 16px" }}>
        <Section title="How the board starts" defaultOpen>
          <p style={paragraph}>
            Each side has 24 pieces. White (Evens) sits at the top; Black (Odds) sits at the
            bottom. Black moves first.
          </p>
          <StartingBoardSVG />
          <ul style={list}>
            <li>
              <b>White (Evens), 24 pieces</b> — 8 Rounds, 8 Triangles, 7 Squares + the White
              King (Pyramid value 91, six layers) at G4.
            </li>
            <li>
              <b>Black (Odds), 24 pieces</b> — 8 Rounds, 8 Triangles, 7 Squares + the Black
              King (Pyramid value 190, five layers) at A13.
            </li>
          </ul>
        </Section>

        <Section title="Pieces and movement" defaultOpen>
          <PieceRow
            shape="●"
            name="Round"
            values="W: 2,4,6,8,4,16,36,64. B: 3,5,7,9,9,25,49,81."
            motion="One step in any direction (orthogonal or diagonal) — eight destinations on an open board."
          />
          <PieceRow
            shape="▲"
            name="Triangle"
            values="W: 6,20,42,72,9,25,49,81. B: 12,30,56,90,16,36,64,100."
            motion="Two squares orthogonally (path clear) — or a flying knight's hop (1,2) that leaps but can't capture."
          />
          <PieceRow
            shape="■"
            name="Square"
            values="W: 15,45,153,25,81,169,289. B: 28,66,120,49,121,225,361."
            motion="Three squares orthogonally (path clear) — or a flying extended-knight (1,3) that leaps but can't capture."
          />
          <PieceRow
            shape="◆"
            name="King (Pyramid)"
            values="W: 91 (6 layers). B: 190 (5 layers)."
            motion="Each turn you declare Round, Triangle, or Square mode. Lose a layer of one shape and the King loses that mode."
          />
        </Section>

        <Section title="Capture methods">
          <CaptureRow
            name="Encounter"
            detail="Your piece is in range of a same-valued enemy. Declare the capture; the enemy is removed."
          />
          <CaptureRow
            name="Assault"
            detail="Two of your pieces both reach one enemy and combine by + − × ÷ to equal that enemy's value."
          />
          <CaptureRow
            name="Siege (Oblivion)"
            detail="An enemy piece is surrounded on all four orthogonal sides by your pieces or board edges. Auto-resolves."
          />
          <CaptureRow
            name="Deceit"
            detail="Same as Encounter, but the attacker advances into the captured square. Choose Hold or Advance in the popup."
          />
        </Section>

        <Section title="Victory conditions">
          <p style={paragraph}>
            <b>Common</b> and <b>Proper</b> reward captures. The progression-based victories
            need pieces arranged in the opponent's first five ranks (the triumph zone), with
            the enemy King taken first.
          </p>
          <VictoryRow level="Common" detail="Captured-piece total reaches the threshold (default 160)." />
          <VictoryRow
            level="Proper"
            detail="Threshold + 4+ pieces across 2+ shapes."
          />
          <VictoryRow
            level="Greater"
            detail="Three pieces in the triumph zone form one progression (AP, GP, or HP)."
          />
          <VictoryRow
            level="Royal"
            detail="Four pieces in the triumph zone form two of the three progression types (overlapping)."
          />
          <VictoryRow
            level="Glorious"
            detail="Four pieces form all three progressions. Canonical: {3, 4, 6, 9}."
          />
        </Section>

        <Section title="The three progressions">
          <p style={paragraph}>
            <b>AP</b>: equal differences (3, 6, 9 — diffs 3, 3).
          </p>
          <p style={paragraph}>
            <b>GP</b>: equal ratios (4, 6, 9 — ratio 3:2).
          </p>
          <p style={paragraph}>
            <b>HP</b>: reciprocals form an AP (3, 4, 6 — recips 1/3, 1/4, 1/6).
          </p>
          <ProgressionDiagram
            set={[3, 4, 6, 9]}
            triples={[
              { kind: "AP", label: "arithmetic", pieces: [3, 6, 9], check: "diffs 3, 3", color: theme.accentSoft },
              { kind: "GP", label: "geometric", pieces: [4, 6, 9], check: "ratios 3/2", color: theme.legalMove },
              { kind: "HP", label: "harmonic", pieces: [3, 4, 6], check: "recip 1/12", color: theme.captureTarget },
            ]}
            caption="Glorious set {3, 4, 6, 9}"
          />
        </Section>

        <Section title="Strategies">
          <StrategyList items={STRATEGIES} />
        </Section>
      </div>
    </div>
  );
}

const expandedToggleButton: React.CSSProperties = {
  background: theme.boardEdge,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 4,
  padding: "2px 8px",
  fontFamily: FONT_RUNE,
  fontSize: 12,
  cursor: "pointer",
  lineHeight: 1,
};

const collapsedButton: React.CSSProperties = {
  background: theme.boardEdge,
  color: theme.accentSoft,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 6,
  width: 28,
  height: 28,
  fontFamily: FONT_RUNE,
  fontSize: 16,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
